import { Form, Head, Link, router } from "@inertiajs/react"
import {
  ArrowLeft,
  BarChart3,
  ImageIcon,
  Pencil,
  Trash2,
  UserPlus,
} from "lucide-react"
import type { MouseEvent } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import { AllCrewBadge } from "@/components/missions/all-crew-badge"
import { AudienceToggle } from "@/components/missions/audience-toggle"
import { BrandMissionCollabsPanelCard } from "@/components/missions/brand-mission-collabs-panel"
import { MissionDashboardFunnel } from "@/components/missions/mission-dashboard-funnel"
import { MissionDashboardMetrics } from "@/components/missions/mission-dashboard-metrics"
import { MissionDashboardStateFilterChips } from "@/components/missions/mission-dashboard-state-filter-chips"
import { RichTextEditor } from "@/components/rich-text-editor"
import { ThumbnailWithFallback } from "@/components/shared/thumbnail-with-fallback"
import { BrandSubmissionsSheet } from "@/components/submissions/brand-submissions-sheet"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import AppLayout from "@/layouts/app-layout"
import { COVER_ASPECT_CLASS } from "@/lib/mission-cover"
import {
  analyticsMissionPath,
  assignMissionPath,
  editMissionPath,
  missionPath,
  missionsPath,
} from "@/routes"
import type {
  MissionDashboardStateFilter,
  MissionShowProps,
} from "@/types/missions"

/**
 * Brand/Missions/Show — per-mission dashboard.
 *
 * OKS-29 originally shipped a flat read-only summary; OKS-34 added the
 * collabs panel; OKS-31 now adds the funnel + metrics + state-filter
 * chips ABOVE the panel — turning Show into the dashboard the operator
 * needs.
 *
 * **Affordances** (preserved from OKS-29/30/34):
 *   - Edit button (drafts only, gated by `can_edit`)
 *   - Delete button (drafts only, gated by `can_destroy`)
 *   - Assign creators (active missions only)
 *
 * **OKS-31 additions:**
 *   - Funnel card (4 ordered stages + muted Issue/Declined trailing
 *     chips) — bars are clickable to mutate `?state=<x>` and narrow the
 *     collabs panel below.
 *   - Metrics row (4 stat tiles, derived rates).
 *   - State-filter chips above the existing OKS-34 collabs panel.
 *
 * Container width: max-w-4xl (≈896px) — bumped from OKS-29's max-w-3xl
 * (≈720px) so the funnel + metrics row read at desktop without crushing.
 * Brief / attachments / collabs panel re-flow inside the wider container
 * (existing OKS-34 mobile layout already responsive).
 *
 * Funnel-bar / chip click handler: issues an Inertia partial reload
 * `only: ['collabs']` — the funnel + metrics stay (server-rendered once
 * per page load), only the collabs panel re-renders for the new filter.
 */
export default function MissionsShow({
  mission,
  can_edit,
  can_destroy,
  can_set_audience,
  collabs,
  funnel,
  metrics,
  submissions_for_open_collab,
}: MissionShowProps) {
  const { t } = useTranslation()

  // Funnel-bar click + state-filter chip click both mutate `?state=<x>`
  // — partial reload `only: ['collabs']` so the funnel + metrics retain
  // their currently-rendered values (no double work).
  const onStateChange = (state: MissionDashboardStateFilter) => {
    const params = state === "all" ? {} : { state }
    router.get(missionPath(mission.id ?? 0), params, {
      preserveState: true,
      preserveScroll: true,
      only: ["collabs"],
    })
  }

  /**
   * OKS-366 — Click-time HEAD-prefetch guard for mission attachments.
   *
   * The attachment URLs (`/brand/missions/:mid/attachments/:aid`) are
   * brand-scoped proxies served by `MissionAttachmentsController`. When
   * the underlying blob exists in the DB but the storage object is
   * missing (orphan AS row — historical residue from the pre-fix
   * Disk-era deploys, or any future S3-side purge), the controller
   * returns 404 (see `MissionAttachmentsController#rescue_from
   * ActiveStorage::FileNotFoundError`). Without this guard a click
   * would open a new tab to the 404 — UX Spec §1 + §9.B asks us to
   * surface a toast on the same page instead.
   *
   * Posture (UX Spec §9.B "lowest-cost"):
   *   - Plain left-click → preventDefault, fetch HEAD, branch on
   *     response.ok. 2xx → `window.open` with the same security floor
   *     as the native anchor (`noopener,noreferrer`). Non-2xx →
   *     `toast.error(missions.show.attachment_unavailable)`. Network
   *     error (CORS / DNS / offline) → fall through and open anyway;
   *     the browser will surface whatever native error applies.
   *   - Modifier-key clicks (ctrl/cmd/shift/alt) + middle-click +
   *     right-click "Open in new tab" + keyboard Enter → all go
   *     through the native anchor flow (`<a href>` stays in the DOM).
   *     The backend's 404 (from the rescue above) is still graceful in
   *     that path — the browser shows its own 404 page.
   *
   * Brand isolation: same brand-scoped URL the anchor renders. The
   * HEAD request inherits the user's session cookie; cross-brand
   * probes already 404 at the controller-scope level (OKS-186
   * defence-in-depth). No isolation widening.
   */
  const onAttachmentClick = async (
    event: MouseEvent<HTMLAnchorElement>,
    url: string,
  ) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return
    }
    event.preventDefault()

    let ok = true
    try {
      const res = await fetch(url, {
        method: "HEAD",
        credentials: "same-origin",
      })
      ok = res.ok
    } catch {
      // Network / CORS failure — let the native path try, browser
      // surfaces whatever applies. Don't toast on transport errors
      // because they're probably temporary.
      ok = true
    }

    if (ok) {
      window.open(url, "_blank", "noopener,noreferrer")
    } else {
      toast.error(t("missions.show.attachment_unavailable"))
    }
  }

  // OKS-35 — Open the BrandSubmissionsSheet for the picked collab.
  // Partial reload `only: ['submissions_for_open_collab']` so the
  // funnel/metrics/collabs panel stay rendered. URL is updated by the
  // Sheet's own URL-sync effect, not here, so this just requests the
  // payload.
  const openSubmissionsSheet = (collabId: number) => {
    router.get(
      missionPath(mission.id ?? 0),
      { submissions: collabId },
      {
        preserveState: true,
        preserveScroll: true,
        only: ["submissions_for_open_collab"],
      },
    )
  }

  const closeSubmissionsSheet = () => {
    router.get(
      missionPath(mission.id ?? 0),
      {},
      {
        preserveState: true,
        preserveScroll: true,
        only: ["submissions_for_open_collab"],
      },
    )
  }

  return (
    <AppLayout>
      <Head title={`${mission.title} — ${t("missions.show.head_title")}`} />
      <div
        className="mx-auto flex w-full max-w-4xl flex-col gap-6 p-6"
        data-testid="mission-show-page"
      >
        {/*
          OKS-609 — contained 16:9 cover hero at the top of the content
          column (inside max-w-4xl, above the title header). Decorative
          (`alt=""`); the `<h1>` below remains the page's semantic heading,
          so the hero carries no semantic weight and precedes it only
          visually. `loading="eager"` (above the fold). The `bg-muted` box
          holds layout via the fixed aspect ratio (no shift) and IS the
          branded fallback backdrop when the cover is null or fails to load.
        */}
        <div
          data-testid="mission-cover-hero"
          className={`bg-muted ${COVER_ASPECT_CLASS} w-full overflow-hidden rounded-lg`}
        >
          <ThumbnailWithFallback
            src={mission.cover_image_display_url}
            alt=""
            loading="eager"
            className="size-full object-cover"
            fallback={
              <div
                data-testid="mission-cover-hero-fallback"
                className="text-muted-foreground flex size-full items-center justify-center"
                aria-hidden="true"
              >
                <ImageIcon className="size-12" />
              </div>
            }
          />
        </div>

        <header className="flex flex-col gap-3">
          <Link
            href={missionsPath()}
            className="text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 text-sm"
            data-testid="missions-show-back-link"
          >
            <ArrowLeft className="size-4" aria-hidden="true" />
            {t("missions.head_title")}
          </Link>
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-2">
              <h1 className="text-foreground text-2xl font-semibold tracking-tight">
                {mission.title}
              </h1>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">
                  {t(`missions.types.${mission.mission_type}`)}
                </Badge>
                <Badge
                  variant={
                    mission.status === "active" ? "default" : "secondary"
                  }
                >
                  {t(`missions.statuses.${mission.status}`)}
                </Badge>
                <AllCrewBadge audience={mission.audience} />
              </div>
              {/*
                OKS-485 — Explicit "Assign to all crew" toggle. Rendered
                only for writers (brand/agency admin); OKS Admin sees the
                badge above but no control. Works for any crew size and
                for both draft + active missions.
              */}
              {can_set_audience && mission.id != null && (
                <AudienceToggle
                  missionId={mission.id}
                  audience={mission.audience}
                />
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {/*
                OKS-63 — Per-mission Analytics link. Routes to
                `analyticsMissionPath(id)` which renders the
                deep-dive page (funnel + creators + KPIs). Visible to
                anyone who can view the Show page (the deep-dive
                policy mirrors `MissionPolicy#show?`).
              */}
              <Button
                asChild
                variant="outline"
                data-testid="mission-view-analytics-button"
              >
                <Link href={analyticsMissionPath(mission.id ?? 0)}>
                  <BarChart3 className="mr-1 size-4" aria-hidden="true" />
                  {t("missions.show.view_analytics_cta")}
                </Link>
              </Button>
              {can_edit && (
                <Button
                  asChild
                  variant="outline"
                  data-testid="mission-edit-button"
                >
                  <Link href={editMissionPath(mission.id ?? 0)}>
                    <Pencil className="mr-1 size-4" aria-hidden="true" />
                    {t("missions.show.edit_cta")}
                  </Link>
                </Button>
              )}
              {/*
                OKS-30 — Assign creators CTA. Active missions link to the
                assignment surface; drafts render a disabled tooltip with
                "Activate to assign" copy.
              */}
              {mission.status === "active" ? (
                <Button
                  asChild
                  variant="default"
                  data-testid="mission-assign-cta"
                >
                  <Link href={assignMissionPath(mission.id ?? 0)}>
                    <UserPlus className="mr-1 size-4" aria-hidden="true" />
                    {t("missions.show.assign_cta")}
                  </Link>
                </Button>
              ) : (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <span
                        aria-disabled="true"
                        className="inline-flex"
                        data-testid="mission-assign-cta-disabled"
                      >
                        <Button disabled variant="outline">
                          {t("missions.show.assign_cta")}
                        </Button>
                      </span>
                    }
                  />
                  <TooltipContent>
                    {t("missions.show.assign_disabled_tooltip")}
                  </TooltipContent>
                </Tooltip>
              )}
              {can_destroy && (
                <Form
                  action={missionPath(mission.id ?? 0)}
                  method="delete"
                  options={{ preserveScroll: true }}
                  className="inline-flex"
                >
                  {({ processing }) => (
                    <Button
                      type="submit"
                      variant="destructive"
                      disabled={processing}
                      onClick={(event) => {
                        if (
                          !window.confirm(t("missions.show.destroy_confirm"))
                        ) {
                          event.preventDefault()
                        }
                      }}
                      data-testid="mission-delete-button"
                    >
                      <Trash2 className="mr-1 size-4" aria-hidden="true" />
                      {t("missions.show.destroy_cta")}
                    </Button>
                  )}
                </Form>
              )}
            </div>
          </div>
        </header>

        <Card data-testid="mission-show-brief">
          <CardHeader>
            <CardTitle>{t("missions.form.brief.label")}</CardTitle>
          </CardHeader>
          <CardContent>
            <RichTextEditor
              name="mission-show-brief-readonly"
              defaultValue={mission.brief}
              editable={false}
              testId="mission-show-brief-editor"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("missions.show.head_title")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <SummaryRow
              label={t("missions.form.product_info.label")}
              value={mission.product_info}
            />
            <SummaryRow
              label={t("missions.form.deadline.label")}
              value={
                mission.deadline
                  ? new Date(mission.deadline).toLocaleDateString()
                  : null
              }
            />
            <SummaryRow
              label={t("missions.form.content_format.label")}
              value={
                mission.content_format
                  ? mission.content_format === "other"
                    ? (mission.content_format_other ??
                      t("missions.formats.other"))
                    : t(`missions.formats.${mission.content_format}`)
                  : null
              }
            />
            <SummaryRow
              label={t("missions.form.hashtags.label")}
              value={
                mission.hashtags ? mission.hashtags.replace(/;/g, ", ") : null
              }
            />
            <SummaryRow
              label={t("missions.form.mentions.label")}
              value={
                mission.mentions ? mission.mentions.replace(/;/g, ", ") : null
              }
            />
            {mission.mission_type === "promotional" && (
              <>
                <SummaryRow
                  label={t("missions.form.promo_code.label")}
                  value={mission.promo_code}
                  testId="mission-show-promo-code"
                />
                <SummaryRow
                  label={t("missions.form.ecommerce_url.label")}
                  value={mission.ecommerce_url}
                  testId="mission-show-ecommerce-url"
                />
              </>
            )}
          </CardContent>
        </Card>

        <Card data-testid="mission-show-attachments">
          <CardHeader>
            <CardTitle>{t("missions.show.attachments_title")}</CardTitle>
          </CardHeader>
          <CardContent>
            {mission.attachments.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {t("missions.show.no_attachments")}
              </p>
            ) : (
              /*
                OKS-186 — Each row is wrapped in an anchor pointing at the
                brand-scoped proxy URL (`attachment.url`, emitted server-
                side by `MissionsController#serialise_attachments`).
                `target="_blank"` keeps the brand admin on the mission
                page; `rel="noopener noreferrer"` is the security floor
                for `_blank` anchors. `title={filename}` ensures the full
                filename remains discoverable when truncated. The URL is
                rendered verbatim from props — NEVER constructed client-
                side; NEVER use `rails_blob_path` / `rails_blob_url`.
              */
              <ul className="flex flex-col gap-2">
                {mission.attachments.map((attachment) => (
                  <li key={attachment.id}>
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={attachment.filename}
                      data-testid="mission-show-attachment-link"
                      onClick={(event) => {
                        // Wrap the async handler in a void-returning
                        // arrow per `@typescript-eslint/no-misused-
                        // promises` — onClick expects void, not
                        // Promise<void>.
                        void onAttachmentClick(event, attachment.url)
                      }}
                      className="border-input hover:bg-muted/50 focus-visible:ring-ring flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors focus-visible:ring-2 focus-visible:outline-none"
                    >
                      <span className="flex-1 truncate">
                        {attachment.filename}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {Math.ceil(attachment.byte_size / 1024)} KB
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* OKS-31 — Per-mission dashboard: funnel + metrics + state-filter
            chips above the OKS-34 collabs panel. Bars / chips mutate
            `?state=<x>` (partial reload `only: ['collabs']`). */}
        <MissionDashboardFunnel
          funnel={funnel}
          declinedCount={metrics.declined}
          issueCount={metrics.issue}
          missionType={mission.mission_type ?? "open"}
          activeState={collabs.state_filter}
          onStageSelect={onStateChange}
        />

        <MissionDashboardMetrics metrics={metrics} />

        <MissionDashboardStateFilterChips
          activeState={collabs.state_filter}
          stateCounts={collabs.state_counts}
          onStateChange={onStateChange}
        />

        {/* OKS-34 — Collabs panel below dashboard. Server-driven; renders
            empty state when the mission has 0 collabs. Read-only when
            `collabs.can_act === false` (admin inspect / wrong role). */}
        <BrandMissionCollabsPanelCard
          panel={collabs}
          missionId={mission.id ?? 0}
          onViewSubmissions={openSubmissionsSheet}
        />

        {/* OKS-35 — Brand submissions Sheet (URL-synced via
            `?submissions=<collab_id>`). Lives on the page so it survives
            the funnel/chip partial reloads. The Sheet's body is
            populated from the `submissions_for_open_collab` page prop;
            null = closed. */}
        <BrandSubmissionsSheet
          open={submissions_for_open_collab !== null}
          collabId={submissions_for_open_collab?.collab_id ?? null}
          collabCreatorHandle={
            submissions_for_open_collab?.creator_handle ?? null
          }
          missionId={mission.id ?? 0}
          submissions={submissions_for_open_collab?.submissions ?? []}
          canSendKudo={submissions_for_open_collab?.can_send_kudo ?? false}
          coupDeCoeur={submissions_for_open_collab?.coup_de_coeur ?? null}
          onOpenChange={(next) => {
            if (!next) closeSubmissionsSheet()
          }}
        />

        <footer className="text-muted-foreground text-xs">
          {t("missions.show.created_by", {
            name: mission.created_by?.name ?? "—",
          })}{" "}
          {t("missions.show.created_at", {
            date: new Date(mission.created_at).toLocaleDateString(),
          })}
        </footer>
      </div>
    </AppLayout>
  )
}

function SummaryRow({
  label,
  value,
  testId,
}: {
  label: string
  value: string | null
  testId?: string
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <span className="text-foreground text-sm">
        {value ?? t("missions.show.empty_field")}
      </span>
    </div>
  )
}
